/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery, useApolloClient } from "react-apollo";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTable, usePagination, useRowSelect, useSortBy } from "react-table";
import { Table, Field, Control, Button, Icon } from "rbx";
import Loading from "../Loading";
import { ALL_ORDERS_VIEW_BY_PAGE_QUERY } from "../../graphql/Orders/Orders.graphql";
import { useLocalStorage } from "../../utils/hooks";
import Pagination from "../DataTable/Pagination";
import { ORDER_VIEW_COLUMNS_ELD } from "../../pages/DispatchProcessing/Orders/columns";
import { downloadCSV } from "../../utils";
import { TableCell } from "../DataTable";

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <input ref={resolvedRef} type="checkbox" {...rest} />;
  }
);

function ReactTable({
  columns,
  data,
  callToAction1,
  callToAction2 = () => {},
  handleExportCSV = () => {},
  printInvoices = () => {},
  onCellClick,
  initialSortBy = [],
  handlePagePaginated,
  setDefaultPageSize,
  defaultPageSize,
  selectedFlatRows,
  onChangeSort,
  pageHeadStart = null
}) {
  const location = useLocation();
  const tableState = {
    pageIndex: 0,
    sortBy: initialSortBy,
    pageSize: defaultPageSize
  };
  const newData = data && Array.isArray(data) ? data : data.Payload || null;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    setPageSize,
    state: { pageSize = defaultPageSize, sortBy }
  } = useTable(
    {
      columns,
      data: newData,
      initialState: { ...tableState }
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const renderTableHeaderSort = column => {
    if (column.isSorted) {
      return column.isSortedDesc ? " ▼" : " ▲";
    }
  };
  const handleSetPageSize = n => {
    setDefaultPageSize(n);
    setPageSize(n);
    handlePagePaginated(null, n);
  };

  useEffect(() => {
    onChangeSort(sortBy);
  }, [onChangeSort, sortBy]);

  const showPaginationControls =
    (!!pageOptions.length && pageOptions.length > 1) ||
    data?.PageInfo?.NextPageUrl ||
    data?.PageInfo?.PriorPageUrl ||
    newData.length === pageSize;

  const prevButtonEnabled = data.PageInfo.StartRow !== 0;

  const nextButtonEnabled = !data.PageInfo.IsLastPage;

  const currentPage = Math.ceil(data.PageInfo.StartRow / defaultPageSize + 1);

  const totalPages = Math.ceil(data.PageInfo.QueryCount / defaultPageSize);
  const onClickPrev = () => handlePagePaginated(-1);
  const onClickNext = () => handlePagePaginated(1);
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">{pageHeadStart}</div>
        <div className="page-head-end">
          {location.pathname.includes("booked") && (
            <Button
              color="primary"
              disabled={
                !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
              }
              size="small"
              onClick={() => callToAction1(selectedFlatRows.map(s => s.RecId))}
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          )}
          {location.pathname.includes("exported") &&
            !location.pathname.includes("accounting") && (
              <Field kind="group">
                <Control>
                  <Button
                    color="info"
                    disabled={
                      !Array.isArray(selectedFlatRows) ||
                      !selectedFlatRows.length
                    }
                    size="small"
                    onClick={() =>
                      callToAction1(selectedFlatRows.map(s => s.RecId))
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="chevron-circle-left" />
                    </Icon>
                    <span>Reset to Booked</span>
                  </Button>
                </Control>
                <Control>
                  <Button
                    color="primary"
                    disabled={
                      !Array.isArray(selectedFlatRows) ||
                      !selectedFlatRows.length
                    }
                    size="small"
                    onClick={() =>
                      callToAction2(selectedFlatRows.map(s => s.RecId))
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-import" />
                    </Icon>
                    <span>Import</span>
                  </Button>
                </Control>
              </Field>
            )}
          {/* {location.pathname.includes("ELD/imported") && (
            <Button
              color="info"
              disabled={
                !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
              }
              size="small"
              onClick={() => callToAction1(selectedFlatRows.map(s => s.RecId))}
            >
              <Icon>
                <FontAwesomeIcon icon="chevron-circle-left" />
              </Icon>
              <span>Reset to Exported</span>
            </Button>
          )} */}
          {/* {location.pathname.includes("verified") &&
            !location.pathname.includes("route") &&
            !location.pathname.includes("billing") && (
              <Button
                color="info"
                disabled={
                  !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
                }
                size="small"
                onClick={() =>
                  callToAction1(selectedFlatRows.map(s => s.RecId))
                }
              >
                <Icon>
                  <FontAwesomeIcon icon="chevron-circle-left" />
                </Icon>
                <span>Reset to Imported</span>
              </Button>
            )} */}
          {location.pathname.includes("route-verified") && (
            <Button
              color="link"
              disabled={
                !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
              }
              size="small"
              onClick={() => callToAction1(selectedFlatRows.map(s => s.RecId))}
            >
              <Icon>
                <FontAwesomeIcon icon="chevron-circle-left" />
              </Icon>
              <span>Reset to Imported from ELD</span>
            </Button>
          )}
          {location.pathname.includes("billing-verified") &&
            !location.pathname.includes("invoice-generation") && (
              <Field kind="group">
                <Control>
                  <Button
                    color="info"
                    disabled={
                      !Array.isArray(selectedFlatRows) ||
                      !selectedFlatRows.length
                    }
                    size="small"
                    onClick={() =>
                      callToAction1(selectedFlatRows.map(s => s.RecId))
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-export" />
                    </Icon>
                    <span>Reset to Route Verified</span>
                  </Button>
                </Control>
                <Control>
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleExportCSV}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-export" />
                    </Icon>
                    <span>Export</span>
                  </Button>
                </Control>
              </Field>
            )}
          {(location.pathname.includes("invoice-generated") ||
            location.pathname.includes("invoice-reprint")) && (
            <Field kind="group">
              <Control>
                <Button
                  color="warning"
                  disabled={
                    !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
                  }
                  size="small"
                  onClick={() =>
                    printInvoices([
                      ...new Set(selectedFlatRows.map(s => s.InvoiceNumber))
                    ])
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="file-export" />
                  </Icon>
                  <span>
                    {location.pathname.includes("invoice-generated")
                      ? "Print"
                      : "Reprint"}{" "}
                    Invoices
                  </span>
                </Button>
              </Control>
              {location.pathname.includes("invoice-generated") && (
                <Control>
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleExportCSV}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="print" />
                    </Icon>
                    <span>Export</span>
                  </Button>
                </Control>
              )}
            </Field>
          )}
          {location.pathname.includes("exported-to-accounting") && (
            <Field kind="group">
              <Control>
                <Button color="primary" size="small" onClick={handleExportCSV}>
                  <Icon>
                    <FontAwesomeIcon icon="file-export" />
                  </Icon>
                  <span>Export</span>
                </Button>
              </Control>
              <Control>
                <Button
                  color="warning"
                  disabled={
                    !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
                  }
                  size="small"
                  onClick={() =>
                    printInvoices(selectedFlatRows.map(s => s.InvoiceNumber))
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="print" />
                  </Icon>
                  <span>Reprint Invoices</span>
                </Button>
              </Control>
            </Field>
          )}
          {location.pathname.includes("billing-verified") &&
            location.pathname.includes("invoice-generation") && (
              <Field kind="group">
                <Control>
                  <Button
                    color="info"
                    disabled={
                      !Array.isArray(selectedFlatRows) ||
                      !selectedFlatRows.length
                    }
                    size="small"
                    onClick={() =>
                      callToAction1(selectedFlatRows.map(s => s.RecId))
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-export" />
                    </Icon>
                    <span>Reset to Route Verified</span>
                  </Button>
                </Control>
                <Control>
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleExportCSV}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-export" />
                    </Icon>
                    <span>Export</span>
                  </Button>
                </Control>
              </Field>
            )}
          {location.pathname.includes("imported-in-accounting") && (
            <Field kind="group">
              <Control>
                <Button color="link" size="small" onClick={callToAction2}>
                  <Icon>
                    <FontAwesomeIcon icon="file-export" />
                  </Icon>
                  <span>Post</span>
                </Button>
              </Control>
              <Control>
                <Button color="primary" size="small" onClick={handleExportCSV}>
                  <Icon>
                    <FontAwesomeIcon icon="file-export" />
                  </Icon>
                  <span>Export</span>
                </Button>
              </Control>
              <Control>
                <Button
                  color="warning"
                  disabled={
                    !Array.isArray(selectedFlatRows) || !selectedFlatRows.length
                  }
                  size="small"
                  onClick={() =>
                    printInvoices(selectedFlatRows.map(s => s.InvoiceNumber))
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="print" />
                  </Icon>
                  <span>Reprint Invoices</span>
                </Button>
              </Control>
            </Field>
          )}
        </div>
      </header>
      <div className="data-table-container">
        <div className="table-container">
          <Table {...getTableProps()} bordered fullwidth hoverable narrow>
            <Table.Head>
              {headerGroups.map(headerGroup => (
                <Table.Row {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <Table.Heading
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render("Header")}
                      <span>{renderTableHeaderSort(column)}</span>
                    </Table.Heading>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <Table.Row {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <Table.Cell
                        {...cell.getCellProps()}
                        onClick={() => onCellClick(cell)}
                      >
                        {cell.render("Cell")}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {/* 
            Pagination can be built however you'd like. 
            This is just a very basic UI implementation:
          */}
        </div>
        {showPaginationControls && (
          <Field align="right" kind="group">
            <Control>
              <Pagination
                canNextPage={nextButtonEnabled}
                canPreviousPage={prevButtonEnabled}
                page={currentPage}
                pageSize={pageSize}
                pages={totalPages || "?"}
                setPageSize={handleSetPageSize}
                onClickNext={onClickNext}
                onClickPrev={onClickPrev}
              />
            </Control>
          </Field>
        )}
      </div>
    </React.Fragment>
  );
}

// ================================================
// ============== ORDERS TABLE ====================
// ================================================

function OrdersTable({
  where,
  callToAction1 = () => {},
  callToAction2 = () => {},
  printInvoices = () => {},
  onCellClick,
  initialSortBy,
  title,
  columns = ORDER_VIEW_COLUMNS_ELD,
  pageHeadStart = null,
  selectedFlatRows = [],
  setSelectedFlatRows = () => {}
}) {
  const client = useApolloClient();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orderByMulti, setOrderByMulti] = useState(initialSortBy);

  // useEffect(() => {
  //   setOrderByMulti(initialSortBy);
  // }, [initialSortBy]);

  const handleChangeSort = useCallback(async sortBy => {
    setOrderByMulti(sortBy);
  }, []);
  const [defaultPageSize, setDefaultPageSize] = useLocalStorage(
    `DEFAULT_PAGE_SIZE`,
    18
  );

  const { data, loading } = useQuery(ALL_ORDERS_VIEW_BY_PAGE_QUERY, {
    variables: {
      where,
      orderByMulti: orderByMulti.map(x => `${x.id}_${x.desc ? "DESC" : "ASC"}`),
      first: defaultPageSize
    },
    pollInterval: 30e3,
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    if (data && data.VW_OrdersByPage && !loading && !result) {
      setResult(data);
    }
  }, [data, loading, result, where]);

  useEffect(() => {
    if (Array.isArray(data?.VW_OrdersByPage?.Payload) && !loading) {
      setResult(data);
    }
  }, [data, loading]);

  const handleExportCSV = () => {
    downloadCSV(
      client,
      {
        query: ALL_ORDERS_VIEW_BY_PAGE_QUERY,
        where,
        orderByMulti: orderByMulti.map(
          x => `${x.id}_${x.desc ? "DESC" : "ASC"}`
        )
      },
      "VW_OrdersByPage",
      columns
    );
  };

  const handlePagePaginated = async (direction, resizePage) => {
    setIsLoading(true);
    const {
      PageInfo: { NextPageUrl, PriorPageUrl }
    } = result?.VW_OrdersByPage;
    const variables = resizePage
      ? {
          where,
          first: resizePage
        }
      : {
          where:
            direction === 1
              ? { ...where, NextPageUrl }
              : { ...where, PriorPageUrl }
        };

    const newData = await client.query({
      query: ALL_ORDERS_VIEW_BY_PAGE_QUERY,
      variables,
      fetchPolicy: "network-only"
    });
    if (
      newData?.data?.VW_OrdersByPage &&
      !newData?.loading &&
      newData?.data?.VW_OrdersByPage.Payload
    ) {
      setResult(newData.data);
    }
    setIsLoading(false);
  };

  const { data: allData } = useQuery(ALL_ORDERS_VIEW_BY_PAGE_QUERY, {
    variables: {
      where,
      orderByMulti: orderByMulti.map(x => `${x.id}_${x.desc ? "DESC" : "ASC"}`)
    },
    fetchPolicy: "network-only"
  });

  const exits =
    result &&
    result?.VW_OrdersByPage?.Payload &&
    Array.isArray(result?.VW_OrdersByPage?.Payload) &&
    result.VW_OrdersByPage.Payload;

  const allToRender =
    allData?.VW_OrdersByPage && Array.isArray(allData?.VW_OrdersByPage)
      ? allData.VW_OrdersByPage || []
      : allData?.VW_OrdersByPage?.Payload || [];

  const memoizedColumns = useMemo(
    () => [
      {
        id: "selection",
        Header: () => {
          const allSelected = useMemo(
            () =>
              selectedFlatRows.length &&
              allToRender.every(order =>
                selectedFlatRows.some(s => s.RecId === order.RecId)
              ),
            []
          );
          const someSelected = useMemo(
            () =>
              selectedFlatRows.length &&
              allToRender.some(order =>
                selectedFlatRows.some(s => s.RecId === order.RecId)
              ),
            []
          );
          return (
            <TableCell.Checkbox
              checked={!!allSelected}
              indeterminate={!allSelected && someSelected}
              onChange={async () => {
                setSelectedFlatRows(prev =>
                  allSelected
                    ? prev.filter(
                        item =>
                          !allToRender.find(
                            toSelect => toSelect.RecId === item.RecId
                          )
                      )
                    : [
                        ...prev,
                        ...allToRender.filter(
                          toSelect =>
                            !prev.find(item => toSelect.RecId === item.RecId)
                        )
                      ]
                );
              }}
            />
          );
        },
        Cell: ({ row }) => (
          <TableCell.Checkbox
            checked={selectedFlatRows.find(
              selected => selected.RecId === row.original.RecId
            )}
            onChange={() =>
              setSelectedFlatRows(prev =>
                prev.find(s => s.RecId === row.original.RecId)
                  ? prev.filter(s => s.RecId !== row.original.RecId)
                  : [...prev, row.original]
              )
            }
            onClick={e => e.stopPropagation()}
          />
        )
      },
      ...columns
    ],
    [allToRender, columns, selectedFlatRows, setSelectedFlatRows]
  );

  if (loading || isLoading || !exits) return <Loading />;
  return (
    <React.Fragment>
      <ReactTable
        callToAction1={callToAction1}
        callToAction2={callToAction2}
        columns={memoizedColumns}
        data={result.VW_OrdersByPage}
        defaultPageSize={defaultPageSize}
        handleExportCSV={handleExportCSV}
        handlePagePaginated={handlePagePaginated}
        initialSortBy={orderByMulti}
        pageHeadStart={pageHeadStart}
        printInvoices={printInvoices}
        selectedFlatRows={selectedFlatRows}
        setDefaultPageSize={setDefaultPageSize}
        setSelectedFlatRows={setSelectedFlatRows}
        title={title}
        onCellClick={onCellClick}
        onChangeSort={handleChangeSort}
      />
    </React.Fragment>
  );
}

OrdersTable.propTypes = {
  where: PropTypes.object,
  callToAction1: PropTypes.func,
  callToAction2: PropTypes.func,
  onCellClick: PropTypes.func,
  title: PropTypes.string,
  columns: PropTypes.array,
  initialSortBy: PropTypes.array,
  printInvoices: PropTypes.func,
  pageHeadStart: PropTypes.node,
  selectedFlatRows: PropTypes.array,
  setSelectedFlatRows: PropTypes.func
};
export default OrdersTable;

// ================================================
// ========= GROUPED BY TRIP ORDERS TABLE =========
// ================================================

export function GroupedOrdersTable({
  columns,
  data,
  callToAction1,
  callToAction2 = () => {},
  onCellClick,
  sortBy = []
}) {
  const location = useLocation();
  const [defaultPageSize, setDefaultPageSize] = useLocalStorage(
    `DEFAULT_PAGE_SIZE`,
    18
  );
  const tableState = {
    pageIndex: 0,
    sortBy,
    pageSize: defaultPageSize
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize = defaultPageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { ...tableState }
    },
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (hooks && hooks.visibleColumns) {
        hooks.visibleColumns.push(() => [
          {
            id: "selection",
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox
                  {...getToggleAllPageRowsSelectedProps()}
                />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ]);
      }
    }
  );
  const renderTableHeaderSort = column => {
    if (column.isSorted) {
      return column.isSortedDesc ? " ▼" : " ▲";
    }
  };
  const handleSetPageSize = n => {
    setDefaultPageSize(n);
    setPageSize(n);
  };
  const showPaginationControls = !!pageOptions.length && pageOptions.length > 1;
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-end">
          {location.pathname.includes("booked") && (
            <Button
              color="primary"
              disabled={!selectedFlatRows?.length}
              size="small"
              onClick={() =>
                callToAction1(selectedFlatRows.map(s => s.original.Orders))
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          )}
          {location.pathname.includes("exported") &&
            !location.pathname.includes("accounting") && (
              <Field kind="group">
                <Control>
                  <Button
                    color="info"
                    disabled={!Array.isArray(selectedFlatRows)}
                    size="small"
                    onClick={() =>
                      callToAction1(
                        selectedFlatRows.map(s => s.original.Orders)
                      )
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="chevron-circle-left" />
                    </Icon>
                    <span>Reset to Booked</span>
                  </Button>
                </Control>
                <Control>
                  <Button
                    color="primary"
                    disabled={!Array.isArray(selectedFlatRows)}
                    size="small"
                    onClick={() =>
                      callToAction2(
                        selectedFlatRows.map(s => s.original.Orders)
                      )
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="file-import" />
                    </Icon>
                    <span>Import</span>
                  </Button>
                </Control>
              </Field>
            )}
        </div>
      </header>
      <div className="data-table-container">
        <div className="table-container">
          <Table {...getTableProps()} bordered fullwidth hoverable narrow>
            <Table.Head>
              {headerGroups.map(headerGroup => (
                <Table.Row {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <Table.Heading
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render("Header")}
                      <span>{renderTableHeaderSort(column)}</span>
                    </Table.Heading>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <Table.Row {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <Table.Cell
                        {...cell.getCellProps()}
                        onClick={() => onCellClick(cell)}
                      >
                        {cell.render("Cell")}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {/* 
            Pagination can be built however you'd like. 
            This is just a very basic UI implementation: This is fine. I don't expect many of these tables to get very large in production.
          */}
        </div>
        {showPaginationControls && (
          <Field align="right" kind="group">
            <Control>
              <Pagination
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                gotoPage={gotoPage}
                page={pageIndex + 1}
                pageSize={pageSize}
                pages={pageOptions.length}
                setPageSize={handleSetPageSize}
                onClickNext={nextPage}
                onClickPrev={previousPage}
              />
            </Control>
          </Field>
        )}
      </div>
    </React.Fragment>
  );
}
